import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Alert } from '@shared/interfaces/alert/alert';
import { ActivationStart, Router } from '@angular/router';
import { filter } from 'rxjs';
import { AlertAreas } from '@shared/components/alert-service-wrapper/alert-areas';

/**
 * A service responsible for managing and displaying alerts across the application.
 * Alerts are tied to specific IDs and can be filtered, added, or removed as needed.
 *
 * Namespaced areas are 'global', 'header' and 'modal' - which are handled automatically.
 */
@Injectable({
  providedIn: 'root'
})
export class AlertService {
  /**
   * Internal tracking of all current alerts.
   */
  private readonly _alerts: WritableSignal<Alert[]> = signal<Alert[]>([]);
  private readonly globalReaderModeAlert: WritableSignal<Alert | null> = signal(null);

  /**
   * Read-only access to the current list of alerts.
   * This ensures alerts can be observed without modifying the state directly.
   */
  public readonly alerts = this._alerts.asReadonly();

  /** @ignore **/
  private readonly router: Router = inject(Router);

  /**
   * Initializes the `AlertService` and subscribes to router events.
   * Clears all alerts when the route changes.
   */
  constructor() {
    /**
     * Clears all active alerts whenever a route activation starts.
     */
    this.router.events
      .pipe(filter(event => event instanceof ActivationStart))
      .subscribe(() => {
        this.clearAll();
      });
  }

  /**
   * Retrieves alerts by a specific location identifier.
   *
   * @param locationId - The unique identifier for the location tied to the alerts.
   * @returns A computed signal of the alerts matching the specified location ID.
   */
  public getAlertsByLocation(locationId: string) {
    return computed(() => {
      const alerts = this._alerts().filter(alert => alert.id === locationId);
      const readerModeAlert = locationId === 'global' ? this.globalReaderModeAlert() : null;

      return readerModeAlert ? [readerModeAlert, ...alerts] : alerts;
    });
  }

  public setGlobalReaderMode(message: string | null): void {
    this.globalReaderModeAlert.set(message
      ? {
          id: 'global',
          type: 'warning',
          message: [message],
          timestamp: Date.now(),
          closeable: false,
        }
      : null);
  }

  /**
   * Adds a new alert or replaces an existing one with the same ID.
   *
   * @param id - A unique identifier for the alert represented by AlertArea
   * @param type - The type of the alert (e.g., 'danger', 'warning', 'info').
   * @param message - The message to display in the alert. Can be a single string or an array of strings.
   * @param closeable - An optional parameter to specify if the alert can be closed manually. Defaults to `false`.
   */
  public open(id: AlertAreas, type: 'danger' | 'warning' | 'info', message: string | string[], closeable: boolean = false): void {
    const newAlert: Alert = {
      id,
      type,
      message: Array.isArray(message) ? message : [message],
      timestamp: Date.now(),
      closeable
    };
    this._alerts.update(alerts => {
      // Remove existing alert(s) with the same ID
      const filtered = alerts.filter(alert => alert.id !== id);
      return [...filtered, newAlert];
    });
  }

  /** Show Shorthand **/
  public show(id: AlertAreas, type: 'danger' | 'warning' | 'info', message: string | string[], closeable: boolean = false): void {
    this.open(id, type, message, closeable);
  }

  /** Shorthands **/
  public warning(id: AlertAreas, message: string | string[], closeable: boolean = false): void {
    this.open(id, 'warning', message, closeable);
  }

  public info(id: AlertAreas, message: string | string[], closeable: boolean = false): void {
    this.open(id, 'info', message, closeable);
  }

  public danger(id: AlertAreas, message: string | string[], closeable: boolean = false): void {
    this.open(id, 'danger', message, closeable);
  }

  /** Global Shorthands **/
  public warningGlobal(message: string | string[], closeable: boolean = false): void {
    this.open('global', 'warning', message, closeable);
  }

  public dangerGlobal(message: string | string[], closeable: boolean = false): void {
    this.open('global', 'danger', message, closeable);
  }

  public infoGlobal(message: string | string[], closeable: boolean = false): void {
    this.open('global', 'info', message, closeable);
  }

  /**
   * Removes an alert with the specified ID.
   *
   * @param id - The unique identifier of the alert to remove.
   */
  public close(id: string): void {
    this._alerts.update(alerts => alerts.filter(alert => alert.id !== id));
  }

  /**
   * Removes a specific alert instance.
   *
   * @param alertToRemove - The alert instance to be removed, identified by its `timestamp`.
   */
  public closeAlert(alertToRemove: Alert): void {
    this._alerts.update(alerts =>
      alerts.filter(alert => alert.timestamp !== alertToRemove.timestamp)
    );
  }

  /**
   * Clears all alerts from the service.
   */
  public clearAll(): void {
    this._alerts.set([]);
  }
}
